import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, LogOut, ShieldCheck, Users, Clock, Plus, LifeBuoy, Plug, TrendingUp, Download, Activity } from "lucide-react";
import OwnerSupport from "@/pages/OwnerSupport";
import IntegrationsPanel from "@/pages/IntegrationsPanel";
import OwnerBilling from "@/pages/OwnerBilling";
import OwnerUsage from "@/pages/OwnerUsage";

const STATUS_STYLE = {
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  ACTIVE_COMPLIMENTARY: "bg-violet-500/10 text-violet-300 border-violet-400/30",
  TRIAL: "bg-sky-500/10 text-sky-300 border-sky-400/30",
  TRIAL_EXPIRED: "bg-amber-500/10 text-amber-300 border-amber-400/30",
  PAST_DUE: "bg-orange-500/10 text-orange-300 border-orange-400/30",
  EXPIRED: "bg-red-500/10 text-red-300 border-red-400/30",
  SUSPENDED: "bg-slate-500/10 text-slate-300 border-slate-400/30",
};

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tenants, setTenants] = useState(null);
  const [err, setErr] = useState("");
  const [extending, setExtending] = useState({});
  const [tab, setTab] = useState("tenants");
  const [supSummary, setSupSummary] = useState({});

  const load = () => http.get("/platform/tenants/full").then((r) => setTenants(r.data)).catch((e) => setErr(apiErr(e)));
  useEffect(() => { load(); http.get("/support/summary").then((r) => setSupSummary(r.data)).catch(() => {}); }, []);

  const extend = async (tid, days) => {
    try { await http.post(`/platform/tenants/${tid}/extend-trial`, { days }); toast.success(`Trial esteso di ${days} giorni`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (tid, status) => {
    try { await http.post(`/platform/tenants/${tid}/set-status`, { status }); toast.success(`Stato impostato: ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const grantFree = async (tid, duration) => {
    try { await http.post(`/platform/tenants/${tid}/grant-free`, { duration }); toast.success("Accesso gratuito concesso"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const suspend = async (tid) => { try { await http.post(`/platform/tenants/${tid}/suspend`); toast.success("Azienda sospesa"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const reactivate = async (tid) => { try { await http.post(`/platform/tenants/${tid}/reactivate`); toast.success("Azienda riattivata"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100" data-testid="platform-dashboard">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-sky-300" /></div>
          <div>
            <div className="font-heading font-extrabold tracking-tight text-lg">NEXTMIND INFISSI</div>
            <div className="text-[10px] uppercase tracking-widest text-sky-300">Platform Console · Fase 2</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-semibold">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wide text-sky-300">{user?.role}</div>
          </div>
          <a data-testid="platform-backup-export-btn" href={`${API}/platform/backup/export`}
             className="hidden sm:inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-slate-300 hover:bg-white/10 transition-colors"
             title="Scarica backup completo (JSON compresso)">
            <Download className="w-4 h-4 mr-2" /> Backup
          </a>
          <Button data-testid="platform-logout-btn" variant="ghost" className="text-slate-300 hover:bg-white/10" onClick={() => { logout(); nav("/login"); }}>
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex flex-wrap gap-2">
          <button data-testid="platform-tab-tenants" onClick={() => setTab("tenants")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${tab === "tenants" ? "bg-sky-500/20 text-sky-300 border-sky-400/40" : "border-white/10 text-slate-400"}`}>
            <Building2 className="w-4 h-4" /> Aziende
          </button>
          <button data-testid="platform-tab-support" onClick={() => setTab("support")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${tab === "support" ? "bg-sky-500/20 text-sky-300 border-sky-400/40" : "border-white/10 text-slate-400"}`}>
            <LifeBuoy className="w-4 h-4" /> Assistenza
            {supSummary.unread > 0 && <span className="ml-1 text-[10px] bg-sky-500 text-white rounded-full px-1.5 py-0.5">{supSummary.unread}</span>}
          </button>
          <button data-testid="platform-tab-integrations" onClick={() => setTab("integrations")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${tab === "integrations" ? "bg-sky-500/20 text-sky-300 border-sky-400/40" : "border-white/10 text-slate-400"}`}>
            <Plug className="w-4 h-4" /> API & Integrazioni
          </button>
          <button data-testid="platform-tab-billing" onClick={() => setTab("billing")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${tab === "billing" ? "bg-sky-500/20 text-sky-300 border-sky-400/40" : "border-white/10 text-slate-400"}`}>
            <TrendingUp className="w-4 h-4" /> Abbonamenti
          </button>
          <button data-testid="platform-tab-usage" onClick={() => setTab("usage")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${tab === "usage" ? "bg-sky-500/20 text-sky-300 border-sky-400/40" : "border-white/10 text-slate-400"}`}>
            <Activity className="w-4 h-4" /> Utilizzo
          </button>
        </div>

        {tab === "integrations" ? <IntegrationsPanel /> : tab === "billing" ? <OwnerBilling /> : tab === "support" ? <OwnerSupport /> : tab === "usage" ? <OwnerUsage /> : (
        <>
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Aziende (Tenant)</h1>
          <p className="text-sm text-slate-400 mt-1">Stato abbonamento/prova, gestione trial. Billing completo nelle fasi successive.</p>
        </div>

        {err && <div className="bg-red-500/10 border border-red-400/30 text-red-300 rounded-lg px-4 py-3 text-sm">{err}</div>}
        {tenants === null && !err && <Loading />}

        {tenants && (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="tenant-list">
            {tenants.map((t) => (
              <div key={t.tenant_id} data-testid="tenant-card" className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-sky-300" /></div>
                    <div>
                      <div className="font-heading font-bold text-base" data-testid="tenant-name">{t.name}</div>
                      <div className="text-xs text-slate-400" data-testid="tenant-slug">/{t.slug}</div>
                    </div>
                  </div>
                  <span data-testid="tenant-status" className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-full border ${STATUS_STYLE[t.status] || STATUS_STYLE.active}`}>{t.status}</span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-300"><Users className="w-4 h-4 text-slate-500" /> <span data-testid="tenant-users">{t.user_count} utenti</span></div>
                  {t.status === "TRIAL" && <div className="flex items-center gap-2 text-sky-300" data-testid="tenant-days"><Clock className="w-4 h-4" /> {t.days_remaining} gg rimasti</div>}
                </div>
                <div className="mt-2 text-xs text-slate-400">Admin: {t.admin_email || "—"}</div>
                {t.trial_ends_at && <div className="text-xs text-slate-500">Trial fino a: {new Date(t.trial_ends_at).toLocaleDateString("it-IT")}</div>}
                <div className="mt-2 text-[10px] font-mono text-slate-500 break-all" data-testid="tenant-id">ID: {t.tenant_id}</div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" data-testid={`extend-3-${t.tenant_id}`} variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10 h-8" onClick={() => extend(t.tenant_id, 3)}>+3</Button>
                  <Button size="sm" data-testid={`extend-7-${t.tenant_id}`} variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10 h-8" onClick={() => extend(t.tenant_id, 7)}>+7</Button>
                  <Button size="sm" data-testid={`extend-14-${t.tenant_id}`} variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10 h-8" onClick={() => extend(t.tenant_id, 14)}>+14</Button>
                  <Input data-testid={`extend-custom-${t.tenant_id}`} type="number" placeholder="gg" className="w-16 h-8 bg-white/5 border-white/20"
                    value={extending[t.tenant_id] || ""} onChange={(e) => setExtending({ ...extending, [t.tenant_id]: e.target.value })} />
                  <Button size="sm" className="bg-sky-500 hover:bg-sky-400 text-[#0B1120] h-8" onClick={() => extend(t.tenant_id, parseInt(extending[t.tenant_id] || "0", 10))}>
                    <Plus className="w-3 h-3 mr-1" /> Estendi
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-white/5 pt-3">
                  <select data-testid={`grant-free-${t.tenant_id}`} defaultValue="" onChange={(e) => { if (e.target.value) { grantFree(t.tenant_id, e.target.value); e.target.value = ""; } }}
                    className="h-8 rounded-md bg-white/5 border border-white/20 text-xs text-slate-100 px-2">
                    <option value="" disabled>🎁 Gratis…</option>
                    <option value="1m">1 mese</option>
                    <option value="3m">3 mesi</option>
                    <option value="6m">6 mesi</option>
                    <option value="1y">1 anno</option>
                    <option value="forever">Senza scadenza</option>
                  </select>
                  {t.status === "SUSPENDED"
                    ? <Button size="sm" data-testid={`reactivate-${t.tenant_id}`} className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => reactivate(t.tenant_id)}>Riattiva</Button>
                    : <Button size="sm" variant="outline" data-testid={`suspend-${t.tenant_id}`} className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10 h-8" onClick={() => suspend(t.tenant_id)}>Sospendi</Button>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button data-testid={`set-trial-${t.tenant_id}`} className="text-[10px] text-slate-400 underline" onClick={() => setStatus(t.tenant_id, "TRIAL")}>test: TRIAL</button>
                  <button data-testid={`set-active-${t.tenant_id}`} className="text-[10px] text-slate-400 underline" onClick={() => setStatus(t.tenant_id, "active")}>test: ACTIVE</button>
                  <button data-testid={`set-expired-${t.tenant_id}`} className="text-[10px] text-slate-400 underline" onClick={() => setStatus(t.tenant_id, "TRIAL_EXPIRED")}>test: EXPIRED</button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
